import { useEffect } from "react";

const GTM_ID = "GTM-PQ2HC24";
const LEGACY_CONSENT_STORAGE_KEY = "cookie-consent";

type StoredConsentState = {
  hasConsented?: boolean;
  preferences?: {
    analytics?: boolean;
    marketing?: boolean;
  };
};

/** Load GTM when the user accepted analytics (GA4) and/or marketing (pixels). */
const hasTrackingConsent = () => {
  const raw = window.localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as StoredConsentState;
    const prefs = parsed.preferences;
    return Boolean(
      parsed.hasConsented && (prefs?.analytics || prefs?.marketing),
    );
  } catch {
    return false;
  }
};

const removeGTM = () => {
  const existingScript = document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
  if (existingScript) existingScript.remove();

  const existingNoscript = document.querySelector('noscript iframe[src*="googletagmanager.com"]');
  if (existingNoscript?.parentElement) existingNoscript.parentElement.remove();

  if (window.dataLayer) window.dataLayer = [];
};

const loadGTM = () => {
  const existingScript = document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
  if (existingScript) return;

  if (!window.dataLayer) window.dataLayer = [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.appendChild(noscript);
};

export const GoogleTagManager = () => {
  useEffect(() => {
    const applyConsent = () => {
      if (hasTrackingConsent()) {
        loadGTM();
      } else {
        removeGTM();
      }
    };

    applyConsent();
    window.addEventListener("consent-updated", applyConsent);
    window.addEventListener("storage", applyConsent);

    return () => {
      window.removeEventListener("consent-updated", applyConsent);
      window.removeEventListener("storage", applyConsent);
    };
  }, []);

  return null;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}
